//reference link:-https://www.npmjs.com/package/react-searchable-select-mui

import * as React from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { BootstrapSelect } from './SelectField';
import {
  Box,
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  PaperProps,
  SelectChangeEvent,
  SelectProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';

export interface IKeyValuePair {
  key: any;
  value: string;
}

interface IBaseProps {
  label?: string;
  searchFieldPlaceholder?: string;
  removeSelectionText?: string;
  helperText?: string;
  formControlProps?: FormControlProps;
  formHelperTextProps?: FormHelperTextProps;
  maxVisibleOptions?: number;
  showAll?: boolean;
  noRemoveSelectionOption?: boolean;
}

interface IDefaultKeyValuePair extends IBaseProps {
  options: IKeyValuePair[];
}

interface ICustomKeyValuePair extends IBaseProps {
  keyPropFn: (option: IKeyValuePair | any) => any;
  valuePropFn: (option: IKeyValuePair | any) => string | number;
  options: any[];
}

export type SearchableSelectProps = (IDefaultKeyValuePair | ICustomKeyValuePair) & SelectProps;

interface ISearchFieldConfig {
  searchFieldPlaceholder?: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  ariaLabel?: string;
  listboxAriaLabel?: string;
  searchInputId?: string;
}

type SearchMenuPaperProps = PaperProps & {
  searchSlot?: React.ReactNode;
};

const SearchMenuPaper = React.forwardRef<HTMLDivElement, SearchMenuPaperProps>(
  function SearchMenuPaper({ children, searchSlot, sx, ...paperProps }, ref) {
    return (
      <Paper
        ref={ref}
        {...paperProps}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxHeight: 'min(320px, 50vh)',
          ...sx,
        }}>
        {searchSlot}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>{children}</Box>
      </Paper>
    );
  }
);

SearchMenuPaper.propTypes = {
  children: PropTypes.node,
  searchSlot: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

function SelectMenuSearchInput({
  searchFieldPlaceholder,
  setQuery,
  ariaLabel,
  searchInputId,
}: ISearchFieldConfig): JSX.Element {
  const accessibleName = ariaLabel || searchFieldPlaceholder || 'Search options';
  const inputId = searchInputId || 'select-menu-search-input';
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <Input
      id={inputId}
      inputRef={inputRef}
      type="search"
      role="searchbox"
      aria-label={accessibleName}
      placeholder={searchFieldPlaceholder || 'Search...'}
      autoComplete="off"
      tabIndex={0}
      startAdornment={
        <InputAdornment position="start">
          <SearchSharpIcon sx={{ color: 'primary.main' }} aria-hidden="true" />
        </InputAdornment>
      }
      onChange={(e) => {
        setQuery(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Tab' || e.key === 'Escape') {
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const menu = e.currentTarget.closest('[role="presentation"]') || document;
          const firstOption = menu.querySelector<HTMLElement>(
            '.MuiMenu-list [role="menuitem"]:not([aria-disabled="true"])'
          );
          firstOption?.focus();
          return;
        }
        // Prevent MUI Select typeahead while typing in search
        e.stopPropagation();
      }}
      sx={{
        width: '100%',
        fontSize: 14,
        px: 1,
        py: 0.5,
        '&:before': {
          borderBottom: '2px solid #767676',
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottom: '2px solid #505050',
        },
        '&:after': {
          borderBottom: '2px solid #303030',
        },
        '& .MuiInput-input': {
          py: 1,
        },
        '&:focus-within': {
          outline: '2px solid #153760',
          outlineOffset: '2px',
        },
      }}
    />
  );
}

function renderSelectSearchInput(config: ISearchFieldConfig): JSX.Element {
  return <SelectMenuSearchInput {...config} />;
}

type SearchableMenuPaperProps = Partial<PaperProps> & {
  component?: React.ElementType;
  searchSlot?: React.ReactNode;
};

export function getSearchableSelectMenuProps(
  searchConfig: ISearchFieldConfig,
  menuProps: SelectProps['MenuProps'] = {}
): SelectProps['MenuProps'] {
  const paperProps: SearchableMenuPaperProps = {
    ...menuProps?.PaperProps,
    component: SearchMenuPaper,
    searchSlot: (
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          const input = e.currentTarget.querySelector('input');
          (input as HTMLInputElement | null)?.focus();
        }}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          px: 2,
          py: 1,
        }}>
        {renderSelectSearchInput(searchConfig)}
      </Box>
    ),
  };

  const listboxLabel =
    menuProps?.MenuListProps?.['aria-label'] ||
    searchConfig.listboxAriaLabel ||
    searchConfig.ariaLabel ||
    'Options';

  return {
    autoFocus: false,
    disableAutoFocusItem: true,
    disableEnforceFocus: true,
    ...menuProps,
    PaperProps: paperProps,
    MenuListProps: {
      disableListWrap: true,
      ...menuProps?.MenuListProps,
      'aria-label': listboxLabel,
    },
  };
}

export function renderFilteredOptions(
  items: { key: string | number; value: string | number }[],
  query: string
): any {
  const filteredOptions =
    items &&
    items.filter &&
    items.filter((item: IKeyValuePair | any) => {
      return (
        !item.key ||
        (item.key && item.key.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1)
      );
    });

  return filteredOptions?.length > 0 ? (
    filteredOptions.map(
      (
        option:
          | {
              key: string | number;
              value: string | number;
            }
          | any
      ) => {
        return (
          <MenuItem
            key={option.key}
            value={option.value}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'common.black',
              '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
            }}>
            <Typography
              sx={{ fontSize: 14, whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}
              component="span">
              {option.key}
            </Typography>
          </MenuItem>
        );
      }
    )
  ) : (
    <MenuItem disabled value="">
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}>
        No Results found
      </Typography>
    </MenuItem>
  );
}

export function SearchableSelect({
  name,
  label,
  items,
  onChange,
  disabled = false,
  searchFieldPlaceholder = '',
  applyRenderValue = false,
  required = false,
  ...props
}: SelectProps & {
  name: string;
  label?: string;
  items: { key: string | number; value: string | number }[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  disabled?: boolean;
  searchFieldPlaceholder?: string;
  applyRenderValue?: boolean;
  required?: boolean;
}): JSX.Element {
  const [query, setQuery] = React.useState('');
  const [field, meta] = useField(name);
  onChange = onChange || field.onChange;
  const errorText = meta.error && meta.touched ? meta.error : '';
  const selectId = `${name}-select`;
  const selectLabelId = `${name}-label`;

  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }} required={required}>
      {label && (
        <InputLabel
          id={selectLabelId}
          shrink
          // htmlFor={id}
          required={required}
          sx={{ transform: 'unset', fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
          {label}
        </InputLabel>
      )}
      <BootstrapSelect
        id={selectId}
        labelId={label ? selectLabelId : undefined}
        sx={{
          mt: '32px',
          ...props.sx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}
        MenuProps={getSearchableSelectMenuProps(
          {
            searchFieldPlaceholder,
            setQuery,
            ariaLabel: label ? `Search ${label}` : 'Search options',
            listboxAriaLabel: label ? `${label} options` : 'Select options',
            searchInputId: `${name}-search`,
          },
          {
            sx: {
              '.MuiPaper-root ': {
                maxWidth: { xs: '100%', sm: '31%', md: '26%' },
                maxHeight: 'min(320px, 50vh)',
                overflowY: 'auto',
              },
            },
          }
        )}
        onClose={() => {
          setQuery('');
        }}
        onOpen={() => {
          window.requestAnimationFrame(() => {
            document.getElementById(`${name}-search`)?.focus();
          });
        }}
        placeholder="ssss"
        {...field}
        onChange={onChange}
        disabled={disabled}
        required={required}
        renderValue={(Selectedvalue) => {
          const selectedKey = items.find((_item) => _item.value == Selectedvalue);
          return (
            <Typography
              sx={{ fontSize: 14, whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}
              component="span">
              {applyRenderValue
                ? (selectedKey?.key as unknown as string)?.split('- ')[1]
                : (selectedKey?.key as unknown as string)}
            </Typography>
          );
        }}
        {...props}>
        {renderFilteredOptions(items, query)}
      </BootstrapSelect>
      {errorText && (
        <FormHelperText error sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
